import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { api } from "@/lib/api-client";
import { useWizardStore } from "@/state/wizard-store";

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

export function Dashboard() {
  const [params] = useSearchParams();
  const questionnaire = useWizardStore.getState().questionnaire;
  const fallbackSite = questionnaire?.domain?.value ?? "";
  const isLodging = questionnaire?.siteType === "lodging";
  const siteId = params.get("site") ?? fallbackSite;
  const currentSiteId = useWizardStore((s) => s.currentSiteId);
  const setCurrentSite = useWizardStore((s) => s.setCurrentSite);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    // Keyed by site so switching shows the right one, not the last one read.
    queryKey: ["subscription", currentSiteId],
    queryFn: api.subscription,
    refetchOnWindowFocus: true,
  });

  const sub = data?.data;
  const sites = data?.sites ?? [];

  // Arriving from a finished build (`?site=<id>`) on an account with several
  // sites: show the one that was just built, not the one last looked at.
  const arrivedSite = params.get("site");
  useEffect(() => {
    if (arrivedSite && arrivedSite !== currentSiteId && sites.some((s) => s.tenant_site_id === arrivedSite)) {
      setCurrentSite(arrivedSite);
    }
    // A remembered site the account no longer has (deleted, or another
    // account on this browser) would read as "no subscription" forever.
    if (currentSiteId && data && sites.length > 0 && !sites.some((s) => s.tenant_site_id === currentSiteId)) {
      setCurrentSite(null);
    }
  }, [arrivedSite, currentSiteId, data, sites, setCurrentSite]);

  function switchSite(siteIdToShow: string) {
    setCurrentSite(siteIdToShow);
    // Every page reads the subscription for "the current site".
    queryClient.invalidateQueries({ queryKey: ["subscription"] });
  }
  // Prefer the server-resolved current domain (post-move aware). Fall back to
  // deriving from tenant_site_id (the SITE_ID) only when it isn't available.
  const siteUrl = sub?.domain
    ? `https://${sub.domain}`
    : sub?.tenant_site_id
    ? sub.tenant_site_id.includes(".")
      ? `https://${sub.tenant_site_id}`
      : `https://${sub.tenant_site_id}.rumio.world`
    : siteId.includes(".")
    ? `https://${siteId}`
    : siteId
    ? `https://${siteId}.rumio.world`
    : null;
  const adminUrl = siteUrl ? `${siteUrl}/admin` : null;

  const trialDays = daysUntil(sub?.trial_ends_at ?? null);
  const isTrial = sub?.status === "trialing";
  const isPastDue = sub?.status === "past_due";
  const isCancelling = sub?.cancel_at_period_end;
  const isSuspended = sub?.status === "suspended";

  return (
    <Shell
      title={isSuspended ? "Subscription paused" : "Your site is live"}
      subtitle={
        isSuspended
          ? "Your trial expired without subscribing. Your data is safe for 30 days — reactivate anytime."
          : isTrial && trialDays !== null
          ? `${trialDays}-day free trial — no charge until then.`
          : isPastDue
          ? "Payment failed. Update your card to keep your site online."
          : "Plan active. Cancel anytime."
      }
    >
      <div className="space-y-4">
        {sites.length > 1 && (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <label
              htmlFor="site-switcher"
              className="text-xs font-medium uppercase tracking-wide"
              style={{ color: "var(--color-muted)" }}
            >
              Your sites
            </label>
            <select
              id="site-switcher"
              className="mt-1 block w-full rounded-md border p-2 text-sm"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
              value={sub?.tenant_site_id ?? currentSiteId ?? ""}
              onChange={(e) => switchSite(e.target.value)}
            >
              {!sub?.tenant_site_id && <option value="">Choose a site…</option>}
              {sites.map((s) => (
                <option key={s.tenant_site_id} value={s.tenant_site_id}>
                  {s.domain ?? `${s.tenant_site_id}.rumio.world`}
                </option>
              ))}
            </select>
          </div>
        )}

        {siteUrl ? (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Your site
            </p>
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block text-lg font-semibold underline"
              style={{ color: "var(--color-primary)" }}
            >
              {siteUrl}
            </a>
            {adminUrl && (
              <a
                href={adminUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block text-sm underline"
                style={{ color: "var(--color-muted)" }}
              >
                Open admin →
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "var(--color-muted)" }}>
            Site URL not available yet — refresh in a moment.
          </p>
        )}

        <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
          <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
            Plan
          </p>
          <p className="mt-1 text-sm">
            {sub ? (
              <>
                You're on the <strong className="capitalize">{sub.plan_slug}</strong> plan.
                {isTrial && trialDays !== null && (
                  <> Trial ends in <strong>{trialDays} day{trialDays === 1 ? "" : "s"}</strong>.</>
                )}
                {isCancelling && sub.current_period_end && (
                  <> Cancels on {new Date(sub.current_period_end).toLocaleDateString()}.</>
                )}
              </>
            ) : (
              "Subscription state loading…"
            )}
          </p>
          {sub?.addons && sub.addons.length > 0 && (
            <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
              Partner products: {sub.addons.map((a) => a.name).join(", ")}.
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <Link to="/subscription">
              <Button variant="secondary">Manage subscription</Button>
            </Link>
          </div>
        </div>

        {!isSuspended && isLodging && (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Import from Airbnb
            </p>
            <p className="mt-1 text-sm">
              Pull your listing details, photos, amenities, and reviews from Airbnb into your new site.
            </p>
            <div className="mt-3">
              <Link to="/import-airbnb">
                <Button variant="secondary">Import my listings</Button>
              </Link>
            </div>
          </div>
        )}

        {!isSuspended && (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Custom domain
            </p>
            <p className="mt-1 text-sm">
              Move your site from its <code>rumio.world</code> address to a domain you own.
            </p>
            <div className="mt-3">
              <Link to="/custom-domain">
                <Button variant="secondary">Set up custom domain</Button>
              </Link>
            </div>
          </div>
        )}

        {sub?.tenant_site_id && (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Another site
            </p>
            <p className="mt-1 text-sm">
              Each site has its own plan. Pick a plan for a new one and we'll build it alongside this one.
            </p>
            <div className="mt-3">
              <Link to="/welcome">
                <Button variant="secondary">Build another site</Button>
              </Link>
            </div>
          </div>
        )}

        {!isSuspended && (
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              What's next
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• Customize your site from the admin</li>
              <li>• Add products, blog posts, and pages</li>
              <li>• Connect Stripe or PayPal for payments</li>
              <li>• Invite teammates as admins</li>
            </ul>
          </div>
        )}
      </div>
    </Shell>
  );
}
